import { motion } from "framer-motion";
import { Server, Cpu, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/landing/primitives";
import { NODE_TIERS } from "@/data/content";

export default function NodeValidator() {
  return (
    <section id="nodes" className="relative py-20 md:py-28 scroll-mt-24" data-testid="section-nodes">
      <div className="container-trt">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: animated node graphic */}
          <Reveal>
            <div className="relative glass-gold rounded-3xl p-8 aspect-square max-w-md mx-auto flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 grid-lines opacity-40" />
              {/* Central validator */}
              <div className="relative">
                <div className="absolute inset-0 -m-8 rounded-full border border-[var(--gold-3)]/30 pulse-ring" />
                <div className="absolute inset-0 -m-8 rounded-full border border-[var(--gold-3)]/30 pulse-ring" style={{ animationDelay: "1.5s" }} />
                <div className="w-24 h-24 rounded-2xl glass-gold flex items-center justify-center gold-glow relative z-10">
                  <Cpu className="w-10 h-10 text-[var(--gold-2)]" />
                </div>
              </div>
              {/* Orbiting nodes */}
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const a = (i / 6) * Math.PI * 2;
                const x = Math.cos(a) * 120;
                const y = Math.sin(a) * 120;
                return (
                  <motion.div
                    key={i}
                    className="absolute w-10 h-10 rounded-xl glass flex items-center justify-center"
                    style={{ left: `calc(50% + ${x}px - 20px)`, top: `calc(50% + ${y}px - 20px)` }}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
                  >
                    <Server className="w-4 h-4 text-[var(--cyan)]" />
                  </motion.div>
                );
              })}
            </div>
          </Reveal>

          {/* Right: content */}
          <div>
            <SectionHeading
              align="left"
              eyebrow="Node Validators"
              title="Secure the network,"
              highlight="earn block rewards"
              sub="TRT runs on Delegated Proof-of-Stake. Operate a node, help validate transactions, and earn a share of block rewards and network fees."
            />
            <div className="mt-8 flex flex-col gap-3">
              {NODE_TIERS.map((n, i) => (
                <Reveal key={n.name} delay={i * 0.1}>
                  <div className="glass rounded-2xl p-5 card-hover flex items-center justify-between gap-4" data-testid={`node-${n.name.toLowerCase().replace(/\s/g, "-")}`}>
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-[var(--gold-2)]" />
                      <div>
                        <div className="font-display font-bold">{n.name}</div>
                        <div className="text-xs font-mono text-[var(--muted)]">{n.reward} · {n.slots}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-mono text-[var(--muted)] uppercase">Required</div>
                      <div className="font-mono font-semibold text-[var(--gold-2)]">{n.stake}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
